import type {
  ClinicCrfPackageV1,
  ClinicCrfPresentationSection,
  DataValidationResult,
  ValidationIssue,
} from '@clinic-crf/contract'

export const MAX_IMPORT_BYTES = 2 * 1024 * 1024

export type IntegrityState = 'checking' | 'verified' | 'mismatch' | 'not-provided'

export interface ImportFileMeta {
  name: string
  size: number
  type?: string
}

export interface ReaderSection extends ClinicCrfPresentationSection {
  generated?: boolean
}

export function validateImportFile(meta: ImportFileMeta): string[] {
  const errors: string[] = []
  if (meta.size <= 0) errors.push('Plik jest pusty.')
  if (meta.size > MAX_IMPORT_BYTES) errors.push('Plik przekracza bezpieczny limit 2 MB.')
  if (!meta.name.toLowerCase().endsWith('.json')) errors.push('Wybierz plik JSON z rozszerzeniem .json.')
  return errors
}

export function buildReaderSections(packageValue: ClinicCrfPackageV1): ReaderSection[] {
  const sections: ReaderSection[] = (packageValue.presentation?.sections ?? []).map((section) => ({ ...section }))
  const assigned = new Set(sections.flatMap((section) => section.field_paths))
  const unassigned = packageValue.definition.fields
    .map((field) => field.path)
    .filter((path) => !assigned.has(path))
  if (unassigned.length) {
    sections.push({
      id: 'reader-unassigned',
      title: 'Pozostałe pola',
      description: 'Pola nieprzypisane do sekcji prezentacji.',
      field_paths: unassigned,
      generated: true,
    })
  }
  if (!sections.length) {
    sections.push({
      id: 'reader-general',
      title: 'Formularz',
      field_paths: packageValue.definition.fields.map((field) => field.path),
      generated: true,
    })
  }
  return sections
}

export function inputDateTimeToIso(value: string): string | undefined {
  if (!value) return undefined
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

export function isoToInputDateTime(value: unknown): string {
  if (typeof value !== 'string') return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}

export function safeDownloadName(value: string): string {
  const normalized = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return normalized || 'formularz'
}

export function integrityLabel(state: IntegrityState): string {
  switch (state) {
    case 'verified':
      return 'Podpis SHA-256 zgodny'
    case 'mismatch':
      return 'Niezgodna suma SHA-256'
    case 'checking':
      return 'Sprawdzanie integralności'
    default:
      return 'Brak sumy w pakiecie'
  }
}

export interface QaReport {
  report_format: 'clinic-crf-qa-report.v1'
  generated_at: string
  privacy: {
    contains_answers: false
    notice: string
  }
  form: {
    code: string
    name: string
    version: number
    definition_version: string
    definition_sha256: string
    package_integrity: IntegrityState
  }
  summary: {
    field_count: number
    visible_field_count: number
    visited_field_count: number
    error_count: number
    active_rule_count: number
    passed_active_rule_count: number
    ready: boolean
  }
  fields: Array<{
    path: string
    label: string
    type: string
    visible: boolean
    required: boolean
    visited: boolean
    error_count: number
    status: 'hidden' | 'not-visited' | 'error' | 'passed'
  }>
  rules: Array<{
    id: string
    path: string
    message: string
    active: boolean
    passed: boolean
  }>
  import_issues: Array<Pick<ValidationIssue, 'path' | 'message' | 'severity' | 'code'>>
}

/** Build a deliberately value-free report. Answer data is not accepted by this function. */
export function buildQaReport(
  packageValue: ClinicCrfPackageV1,
  validation: DataValidationResult,
  visited: ReadonlySet<string>,
  definitionHash: string,
  integrity: IntegrityState,
  importIssues: ValidationIssue[] = [],
  now = new Date(),
): QaReport {
  const visibleFields = validation.fields.filter((entry) => entry.visible)
  const activeRules = validation.rules.filter((entry) => entry.active)
  const errorCount = Object.values(validation.errors).reduce((sum, errors) => sum + errors.length, 0)
  const visitedVisibleCount = visibleFields.filter((entry) => visited.has(entry.field.path)).length
  return {
    report_format: 'clinic-crf-qa-report.v1',
    generated_at: now.toISOString(),
    privacy: {
      contains_answers: false,
      notice: 'Raport zawiera wyłącznie metadane QA. Odpowiedzi testowe nie są eksportowane.',
    },
    form: {
      code: packageValue.form.code,
      name: packageValue.form.name,
      version: packageValue.form.version,
      definition_version: packageValue.definition.version,
      definition_sha256: definitionHash,
      package_integrity: integrity,
    },
    summary: {
      field_count: validation.fields.length,
      visible_field_count: visibleFields.length,
      visited_field_count: visitedVisibleCount,
      error_count: errorCount,
      active_rule_count: activeRules.length,
      passed_active_rule_count: activeRules.filter((entry) => entry.passed).length,
      ready: validation.isValid && visibleFields.every((entry) => visited.has(entry.field.path)),
    },
    fields: validation.fields.map((entry) => {
      const wasVisited = visited.has(entry.field.path)
      const status = !entry.visible
        ? 'hidden'
        : !wasVisited
          ? 'not-visited'
          : entry.errors.length
            ? 'error'
            : 'passed'
      return {
        path: entry.field.path,
        label: entry.field.label,
        type: entry.field.type,
        visible: entry.visible,
        required: entry.required,
        visited: wasVisited,
        error_count: entry.errors.length,
        status,
      }
    }),
    rules: validation.rules.map((entry) => ({
      id: entry.rule.id,
      path: entry.rule.path,
      message: entry.rule.message,
      active: entry.active,
      passed: entry.passed,
    })),
    import_issues: importIssues.map(({ path, message, severity, code }) => ({ path, message, severity, code })),
  }
}
